import React, { useEffect, useRef, useState } from 'react';
import { db } from '../../services/supabase';
import { diagnostic } from '../../utils/diagnostics';
import { AGE_GROUP_OPTIONS } from '../../utils/ageGroups';
import { useFamilyStatus } from '../../contexts/FamilyStatusContext';
import Input from '../UI/Input';
import Button from '../UI/Button';
import './ChildProfileEditModal.css';
const SUFFIX_OPTIONS = ['', 'Jr.', 'Sr.', 'II', 'III', 'IV', 'V'];
const ProfileDialog = ({ busy, onClose, children }) => {
  const dialog = useRef(null);
  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => { element.close(); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, []);
  return <dialog ref={dialog} aria-labelledby="edit-child-profile-title"
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
    className="child-profile-dialog m-auto w-[calc(100%-2rem)] max-w-xl rounded-2xl border border-gray-200 bg-white p-5 text-gray-900 shadow-2xl dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">{children}</dialog>;
};
const ChildProfileEditModal = ({
  child,
  onClose,
  onSaved,
}) => {
  const { isFamilyInactive, canWrite } = useFamilyStatus();
  const locked = isFamilyInactive || canWrite === false || child?.status !== 'active';
  const saving = useRef(false);
  const [
    formData,
    setFormData,
  ] = useState({
    first_name: '',
    middle_initial: '',
    last_name: '',
    suffix: '',
    nickname: '',
    age_group: '',
  });

  const [
    error,
    setError,
  ] = useState('');

  const [
    submitting,
    setSubmitting,
  ] = useState(false);


  useEffect(() => {
    setFormData({
      first_name:
        child?.first_name || '',
      middle_initial:
        child?.middle_initial || '',
      last_name:
        child?.last_name || '',
      suffix:
        child?.suffix || '',
      nickname:
        child?.nickname || '',
      age_group:
        child?.age_group || '',
    });

    setError('');
  }, [
    child,
  ]);


  if (!child) {
    return null;
  }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } = event.target;

      setFormData(
        (current) => ({
          ...current,
          [name]: value,
        })
      );
    };


  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };


  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (saving.current || locked) {
        return;
      }

      setError('');

      const profile = {
        first_name:
          formData.first_name.trim(),
        middle_initial:
          formData.middle_initial
            .trim()
            .toUpperCase(),
        last_name:
          formData.last_name.trim(),
        suffix:
          formData.suffix,
        nickname:
          formData.nickname.trim(),
        age_group:
          formData.age_group,
      };

      if (!profile.first_name) {
        setError(
          'First name is required.'
        );
        return;
      }

      if (
        profile.first_name.length > 50
      ) {
        setError(
          'First name must be 50 characters or fewer.'
        );
        return;
      }

      if (!profile.last_name) {
        setError(
          'Last name is required.'
        );
        return;
      }

      if (
        profile.last_name.length > 50
      ) {
        setError(
          'Last name must be 50 characters or fewer.'
        );
        return;
      }

      if (
        profile.middle_initial &&
        !/^[A-Za-z]$/.test(
          profile.middle_initial
        )
      ) {
        setError(
          'Middle initial must be a single letter.'
        );
        return;
      }

      if (!profile.nickname) {
        setError(
          'Nickname / display name is required.'
        );
        return;
      }

      if (
        profile.nickname.length > 50
      ) {
        setError(
          'Display name must be 50 characters or fewer.'
        );
        return;
      }

      if (
        !AGE_GROUP_OPTIONS.some(
          (option) =>
            option.value ===
            profile.age_group
        )
      ) {
        setError(
          'Please select an age group.'
        );
        return;
      }

      saving.current = true;
      setSubmitting(true);

      try {
        const updatedChild =
          await db.updateChildProfile(
            child.id,
            profile
          );

        await onSaved(
          updatedChild
        );

        onClose();
      } catch (saveError) {
        diagnostic('error', 'family.child_profile_update_failed');

        setError(
          saveError?.message ||
            'Unable to update this child.'
        );
      } finally {
        saving.current = false;
        setSubmitting(false);
      }
    };


  return (
    <ProfileDialog busy={submitting} onClose={handleClose}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2
              id="edit-child-profile-title"
              className="text-xl font-bold text-gray-900 dark:text-slate-100"
            >
              Edit Child Profile
            </h2>

            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Update names and age group without changing the permanent Login ID.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 text-2xl leading-none disabled:cursor-not-allowed"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <Input
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            maxLength={50}
            autoComplete="off"
            disabled={submitting || locked}
            required
          />

          <Input
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            maxLength={50}
            autoComplete="off"
            disabled={submitting || locked}
            required
          />

          <details className="sm:col-span-2 rounded-lg border border-gray-200 p-3 dark:border-slate-600">
            <summary className="cursor-pointer text-sm font-bold text-teal-800 dark:text-teal-300">Additional name details (optional)</summary>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Input
              label="Middle Initial (optional)"
              name="middle_initial"
              value={formData.middle_initial}
              onChange={(event) =>
                setFormData(
                  (current) => ({
                    ...current,
                    middle_initial:
                      event.target.value
                        .replace(
                          /[^A-Za-z]/g,
                          ''
                        )
                        .slice(0, 1)
                        .toUpperCase(),
                  })
                )
              }
              maxLength={1}
              autoComplete="off"
              disabled={submitting || locked}
            />

            <div>
              <label
                htmlFor="edit-child-suffix"
                className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
              >
                Suffix (optional)
              </label>

              <select
                id="edit-child-suffix"
                name="suffix"
                value={formData.suffix}
                onChange={handleChange}
                disabled={submitting || locked}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 border-gray-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
              >
                {SUFFIX_OPTIONS.map(
                  (suffix) => (
                    <option
                      key={suffix || 'none'}
                      value={suffix}
                    >
                      {suffix || 'None'}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          </details>
          <Input
            label="Nickname / Display Name"
            name="nickname"
            value={formData.nickname}
            onChange={handleChange}
            maxLength={50}
            helperText="This is shown on the kid dashboard. Changing it does not change the Login ID."
            disabled={submitting || locked}
            required
          />

          <div>
            <label
              htmlFor="edit-child-age-group"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
            >
              Age Group
            </label>

            <select
              id="edit-child-age-group"
              name="age_group"
              value={formData.age_group}
              onChange={handleChange}
              disabled={submitting || locked}
              required
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 border-gray-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
            >
              <option value="">
                Select an age group
              </option>

              {AGE_GROUP_OPTIONS.map(
                (option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="sm:col-span-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Permanent Login ID
            </div>
            <div className="mt-1 font-mono font-bold text-gray-900 dark:text-slate-100 break-all">
              {child.user_id}
            </div>
            <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-300">
              This cannot be edited and will remain the same after saving.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="sm:col-span-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/35 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              {error}
            </div>
          )}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="primary"
              disabled={submitting || locked}
            >
              {submitting
                ? 'Saving...'
                : 'Save Changes'}
            </Button>
          </div>
        </form>
    </ProfileDialog>
  );
};



export default ChildProfileEditModal;
